import { createContext, useContext, useState, useEffect } from 'react';
import { useRouter } from 'next/router';

import { hasLocalStorage, formatPathname } from 'utils/helpers';
import {
  SEVENROOMS_BASE_URL,
  SEVENROOMS_ALL_LOCATIONS_ID,
} from 'utils/constants';

export const ClaysLocationContext = createContext({});

const SEVENROOMS_FALLBACK_URL = `${SEVENROOMS_BASE_URL}/${SEVENROOMS_ALL_LOCATIONS_ID}`;

const ClaysLocationProvider = ({ children }) => {
  const [claysLocation, setClaysLocation] = useState(null);
  const [allClaysLocations, setAllClaysLocations] = useState([]);
  const [isLocationGatePage, setIsLocationGatePage] = useState(false);
  const [sevenroomsUrl, setSevenroomsUrl] = useState(SEVENROOMS_FALLBACK_URL);

  const {
    asPath,
    query: { location },
  } = useRouter();

  // Fetch all location data
  useEffect(() => {
    const getAllClaysLocations = async () => {
      try {
        const endpointUrl = `/api/all-clays-locations`;
        const data = await fetch(endpointUrl);
        const json = await data.json();

        setAllClaysLocations(json.data || []);
      } catch (e) {
        console.error(e);
      }
    };

    getAllClaysLocations();
  }, []);

  // Store selected location
  useEffect(() => {
    if (location) {
      setClaysLocation(location);

      if (hasLocalStorage()) {
        localStorage.setItem('claysLocation', location);
      }
    }

    if (!location && hasLocalStorage()) {
      setClaysLocation(localStorage.claysLocation);
    }
  }, [location]);

  useEffect(() => {
    setIsLocationGatePage(formatPathname(asPath) === '/');
  }, [asPath]);

  // Create Sevenrooms URL
  useEffect(() => {
    const handleSevenroomsUrl = () => {
      const allVenueIds =
        allClaysLocations
          .map(({ sevenroomsVenueId }) => sevenroomsVenueId)
          .filter(Boolean) || [];

      const venuesParam =
        allVenueIds.length > 0 ? `?venues=${allVenueIds.join(',')}` : '';

      const venueId =
        allClaysLocations.find(({ slug }) => slug === claysLocation)
          ?.sevenroomsVenueId || SEVENROOMS_ALL_LOCATIONS_ID;

      const selectedVenue = isLocationGatePage
        ? SEVENROOMS_ALL_LOCATIONS_ID
        : venueId;

      setSevenroomsUrl(`${SEVENROOMS_BASE_URL}/${selectedVenue}${venuesParam}`);
    };

    if (allClaysLocations?.length) {
      handleSevenroomsUrl();
    }
  }, [claysLocation, allClaysLocations, isLocationGatePage]);

  return (
    <ClaysLocationContext.Provider
      value={{
        claysLocation,
        allClaysLocations,
        sevenroomsUrl,
        isLocationGatePage,
      }}
    >
      {children}
    </ClaysLocationContext.Provider>
  );
};

export const useClaysLocation = () => useContext(ClaysLocationContext);

export default ClaysLocationProvider;
